import { Component, OnInit, Input, ViewChild, ElementRef } from '@angular/core';
import { SurveyPreviewService, SurveyData, SurveyGroup, SurveyMatrix } from '../_services/survey-preview.service';
import { Questions } from '../common-class/questions';
import { Answers } from '../common-class/answers';
import { ParticipationDataService } from '../_services/participation-data.service';
import { SurveyMessages } from '../common-class/survey-messages';
import { Group, Matrix } from '../common-class/pages';

interface QuestionTypeResult {
  strQType: string;
  intQSubType: number;
  intRSType: number;
  intRSFormat: number;
}

interface PreviewSettings {
  skipMandatory: boolean;
  showHiddenQuestions: boolean;
  bypassLogic: boolean;
  surveyFormat: number;
}

@Component({
  selector: 'app-survey-preview',
  templateUrl: './survey-preview.component.html',
  styleUrls: ['./survey-preview.component.scss']
})
export class SurveyPreviewComponent implements OnInit {
  @Input() surveyJsonData: string = '';
  @Input() apiEndpoint: string = '';
  @Input() filePath: string = '';
  
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  
  surveyData: SurveyData | null = null;
  currentPageIndex: number = 0;
  currentPage: any = null;
  showWelcome: boolean = true;
  showThankYou: boolean = false;
  showQuestions: boolean = false;
  
  // Data source selection
  dataSource: 'api' | 'file' | 'json' | 'path' = 'path';
  
  // API and file inputs
  apiUrl: string = '';
  corpno: string = '';
  selectedFile: File | null = null;
  jsonInput: string = '';
  filePathInput: string = 'assets/ai_survey1.json';
  
  // Loading and error states
  isLoading: boolean = false;
  errorMessage: string = '';
  
  // File validation
  isFileValid: boolean = false;
  fileValidationMessage: string = '';

  // Preview settings
  previewSettings: PreviewSettings = {
    skipMandatory: false,
    showHiddenQuestions: false,
    bypassLogic: false,
    surveyFormat: 0
  };

  // Viewport settings
  currentViewport: string = 'web';

  // Survey width settings
  surveyWidthType: number = 3; // Default to full width
  isTablet: boolean = false;
  isMobile: boolean = false;

  // Store converted Questions class data
  convertedQuestions: Questions[] = [];

  // Survey messages from service
  surveyMessages: SurveyMessages;

  // RTL support
  isRTL: boolean = false;

  constructor(private surveyPreviewService: SurveyPreviewService,
        private service: ParticipationDataService
  ) {
    this.surveyMessages = this.surveyPreviewService.getSurveyMessages();
  }

  ngOnInit(): void {
    // Extract corpno from URL query parameters
    this.extractCorpnoFromUrl();

    this.service.setdropDownCount(10);
    this.service.setMDDdropDownCount(50);
    this.service.GroupType="Preview";
    this.loadDefaultSurveyMessages();
    this.service.setSurveyMessages(this.surveyMessages);
    this.service.setPreviewData(this.surveyPreviewService.getPreviewData());

    // Check device type and initialize viewport
    this.checkDeviceType();
    this.checkViewport();

    // Initialize CommonSetting for the service to prevent errors in MultipleTextBoxComponent
    const commonSetting = {
      isMobileDevice: this.isMobile,
      isTablet: this.isTablet,
      isEmbedMobile: 0,
      isEmbedPopMobile: 0,
      isEmbedPopWeb: 0,
      embedLangOpt: 0,
      productName: '',
      strTick: '',
      cp_no: 0,
      dropDownCount: 10,
      ImageFolder: '/assets/images/',
      SurveyURL: '',
      cookieCorpNO: '',
      MDDdropDownCount: 50,
      SurveyFileUploadFolder: '/uploads/', // Default upload folder
      isEmbedNoScroll: false,
      embedDefaultPx: 0,
      embedPopQSpacer: 0,
      isEmbed: 0,
      embedID: 0
    };
    this.service.setCommonSetting(commonSetting);

    this.loadSurveyData();

    // Add window resize listener for responsive behavior
    window.addEventListener('resize', () => {
      this.checkDeviceType();
    });
  }

  /**
   * Extract corpno from URL query parameters
   */
  private extractCorpnoFromUrl(): void {
    if (typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      const corpnoParam = urlParams.get('corpno');
      if (corpnoParam) {
        this.corpno = corpnoParam;
        console.log('Extracted corpno from URL:', this.corpno);
      }
    }
  }

  /**
   * Check device type for responsive behavior
   */
  checkDeviceType(): void {
    const width = window.innerWidth;
    this.isMobile = width <= 768;
    this.isTablet = width > 768 && width <= 1024;
  }

  /**
   * Clean up event listeners
   */
  ngOnDestroy(): void {
    window.removeEventListener('resize', () => {
      this.checkDeviceType();
    });
  }

  /**
   * Get survey width classes based on settings and device type
   */
  getSurveyWidthClasses(): string {
    this.surveyWidthType = 2;
    if (this.isTablet) {
      return 'full-survey-width';
    } else if (this.isMobile) {
      return 'full-survey-width';
    } else {
      switch (this.surveyWidthType) {
        case 1:
          return 'small-survey-width';
        case 2:
          return 'medium-survey-width';
        case 3:
        default:
          return 'full-survey-width';
      }
    }
  }

  /**
   * Get footer width classes for navigation buttons
   */
  getFooterWidthClasses(): string {
    if (this.isTablet) {
      return 'full-survey-width-footer';
    } else if (this.isMobile) {
      return 'full-survey-width-footer';
    } else {
      switch (this.surveyWidthType) {
        case 1:
          return 'small-survey-width-footer';
        case 2:
          return 'medium-survey-width-footer';
        case 3:
        default:
          return 'full-survey-width-footer';
      }
    }
  }

  /**
   * AI-powered question type determination based on question title and description
   * (Kept for future use - not currently used in preview rendering)
   */
  getAIQuestionType(
    strQTitle: string,
    strQTypeDescription: string,
    strSubQuestionType: string
  ): QuestionTypeResult {
    let isMatrix = false;
    let isMulti = false;

    let result: QuestionTypeResult = {
      strQType: "",
      intQSubType: 0,
      intRSType: 0,
      intRSFormat: 0,
    };

    if (strQTypeDescription.includes("grid")) {
      strQTypeDescription = strSubQuestionType;
      isMatrix = true;
    }

    if (strQTypeDescription.includes("signature")) {
      isMatrix = true;
      isMulti = true;
      result.strQType = "T";
      result.intQSubType = 72;
    } else if (strQTypeDescription.includes("attachment")) {
      isMatrix = true;
      isMulti = true;
      result.strQType = "T";
      result.intQSubType = 71;
    } else if (
      strQTypeDescription.includes("multiple text box") ||
      strQTypeDescription.includes("multiple textbox")
    ) {
      isMatrix = true;
      isMulti = true;
      result.strQType = "T";
      result.intQSubType = 0; // Default subtype for multiple text box
    } else if (
      strQTypeDescription.includes("textbox") ||
      strQTypeDescription.includes("text box") ||
      strQTypeDescription.includes("text")
    ) {
      result.strQType = "T";
    } else if (
      strQTypeDescription.includes("dropdown") ||
      strQTypeDescription.includes("drop.down") ||
      strQTypeDescription.includes("drop down") ||
      strQTypeDescription.includes("drop-down")
    ) {
      result.strQType = "L";
      if (isMatrix) {
        result.strQType = "RW";
        result.intQSubType = 1;
      }
    } else if (
      strQTypeDescription.includes("checkbox") ||
      strQTypeDescription.includes("check box")
    ) {
      result.strQType = "C";
      if (isMatrix) {
        result.strQType = "GC";
      }
    }
    // Normal Rating Scale Smiley
    else if (
      strQTypeDescription.includes("smiley") ||
      strQTypeDescription.includes("Rating Smiley")
    ) {
      result.strQType = "RS";
      result.intRSType = 0;
      result.intRSFormat = 2;
    }
    // Normal Rating Scale
    else if (
      strQTypeDescription.includes("rating scale") ||
      strQTypeDescription.includes("ratingslider") ||
      strQTypeDescription.includes("rating slider") ||
      strQTitle.includes("scale of 1 to 10") ||
      strQTitle.includes("scale of 1 to 5")
    ) {
      result.strQType = "RS";
      result.intRSType = 0;
      result.intRSFormat = 0;
      if (isMatrix) {
        result.strQType = "RW";
        result.intQSubType = 1;
      }
    }
    // NPS
    else if (
      strQTypeDescription.includes("nps") ||
      strQTypeDescription.includes("npsscore") ||
      strQTypeDescription.includes("net promoter score") ||
      strQTypeDescription.includes("recommendation question")
    ) {
      result.strQType = "RS";
      result.intRSType = 2;
      result.intRSFormat = 3;
    }
    // CSAT
    else if (
      strQTypeDescription.includes("csat") ||
      strQTypeDescription.includes("csat score") ||
      strQTypeDescription.includes("customer satisfaction score") ||
      strQTypeDescription.includes("(customer satisfaction score)")
    ) {
      result.strQType = "RS";
      result.intRSType = 4;
      result.intRSFormat = 2;
    }
    // CES
    else if (
      strQTypeDescription.includes("ces") ||
      strQTypeDescription.includes("ces score") ||
      strQTypeDescription.includes("customer effort score") ||
      strQTypeDescription.includes("(customer effort score)")
    ) {
      result.strQType = "RS";
      result.intRSType = 3;
      result.intRSFormat = 2;
    } else if (
      strQTypeDescription.includes("ratingradio") ||
      strQTypeDescription.includes("rating radio") ||
      strQTypeDescription.includes("rating grid")
    ) {
      result.strQType = "RT";
      result.intQSubType = 7;
      if (isMatrix) {
        result.strQType = "RW";
        result.intQSubType = 1;
      }
    } else if (
      strQTypeDescription.includes("ratingdrpodown") ||
      strQTypeDescription.includes("rating drpodown")
    ) {
      result.strQType = "RT";
      result.intQSubType = 0;
      if (isMatrix) {
        result.strQType = "RW";
        result.intQSubType = 2;
      }
    } else if (
      strQTypeDescription.includes("horizontal radio button") ||
      strQTypeDescription === "horizontal radio"
    ) {
      result.strQType = "HR";
      if (isMatrix) {
        result.strQType = "GR";
      }
    } else if (
      strQTypeDescription.includes("radiobutton") ||
      strQTypeDescription === "radio button" ||
      strQTypeDescription === "radio"
    ) {
      result.strQType = "R";
      if (isMatrix) {
        result.strQType = "GR";
      }
    }

    return result;
  }

  /**
   * Check and set initial viewport
   */
  checkViewport(): void {
    this.currentViewport = 'web';
    this.updateViewportClasses();
  }

  /**
   * Update viewport CSS classes
   */
  updateViewportClasses(): void {
    const deviceWrapper = document.getElementById('deviceWrapper');
    if (deviceWrapper) {
      deviceWrapper.classList.remove('preview--web', 'preview--mobile', 'preview--tablet');
      deviceWrapper.classList.add(`preview--${this.currentViewport}`);
      
      console.log('Viewport classes updated:', deviceWrapper.className);
    } else {
      console.log('Device wrapper not found');
    }
  }

  /**
   * Load viewport (Desktop, Tablet, Mobile)
   */
  loadViewPort(viewport: string): void {
    console.log('Loading viewport:', viewport);
    this.currentViewport = viewport;
    
    // Update device flags based on viewport
    if (viewport === 'mobile') {
      this.isMobile = true;
      this.isTablet = false;
    } else if (viewport === 'tablet') {
      this.isMobile = false;
      this.isTablet = true;
    } else {
      this.isMobile = false;
      this.isTablet = false;
    }
    
    this.updateViewportClasses();
    
    // Update active icon
    const allIcons = document.querySelectorAll('.preview-container');
    allIcons.forEach(icon => icon.classList.remove('active-icon'));
    
    const targetIcon = document.getElementById(`vr_${viewport}`);
    if (targetIcon) {
      targetIcon.classList.add('active-icon');
    }
    
    console.log('Viewport loaded:', this.currentViewport, 'isMobile:', this.isMobile, 'isTablet:', this.isTablet);
  }

  /**
   * Toggle settings panel
   */
  toggleSettings(): void {
    const settingsContainer = document.getElementById('hrz-preview-container');
    if (settingsContainer) {
      const isVisible = settingsContainer.style.display !== 'none';
      settingsContainer.style.display = isVisible ? 'none' : 'block';
    }
  }

  /**
   * Show format list dropdown
   */
  formListHrzShow(): void {
    const formatList = document.getElementById('hrz-formatList');
    if (formatList) {
      formatList.style.display = 'block';
    }
  }

  /**
   * Set format selection
   */
  setFormatSelect(event: any, customVal?: string): void {
    event.preventDefault();
    event.stopPropagation();
    
    let surveyFormat = customVal ? parseInt(customVal) : parseInt(event.target.value);
    this.previewSettings.surveyFormat = surveyFormat;
    
    // Hide format list
    const formatList = document.getElementById('hrz-formatList');
    if (formatList) {
      formatList.style.display = 'none';
    }
    
    // Hide settings container after delay
    setTimeout(() => {
      const settingsContainer = document.getElementById('hrz-preview-container');
      if (settingsContainer) {
        settingsContainer.style.display = 'none';
      }
    }, 300);
  }

  /**
   * Get format display name
   */
  getFormatDisplayName(format: number): string {
    switch (format) {
      case 0: return 'Classic';
      case 1: return 'Compact';
      case 2: return 'Comfortable';
      default: return 'Classic';
    }
  }

  /**
   * Open in new tab
   */
  openInNewTab(): void {
    const currentUrl = window.location.href;
    window.open(currentUrl, '_blank');
  }

  /**
   * Load survey data based on the current data source
   */
  loadSurveyData(): void {
    this.isLoading = true;
    this.errorMessage = '';
    
    switch (this.dataSource) {
      case 'api':
        this.loadFromAPI();
        break;
      case 'file':
        this.loadFromFile();
        break;
      case 'json':
        this.loadFromJSONString();
        break;
      case 'path':
        this.loadFromFilePath();
        break;
      default:
        this.loadFromInput();
    }
  }

  /**
   * Load survey data from API endpoint
   */
  private loadFromAPI(): void {
    if (!this.apiUrl.trim()) {
      this.handleError('Please enter a valid API endpoint');
      return;
    }

    this.surveyPreviewService.getSurveyFromAPI(this.apiUrl.trim(), this.corpno.trim()).subscribe({
      next: (data) => {
        this.surveyData = data;
        this.initializeSurvey(data);
      },
      error: (error) => {
        this.handleError(`API Error: ${error.message}`);
      }
    });
  }

  /**
   * Load survey data from uploaded file
   */
  private loadFromFile(): void {
    if (!this.selectedFile) {
      this.handleError('Please select a file to upload');
      return;
    }

    this.surveyPreviewService.loadSurveyFromFile(this.selectedFile).subscribe({
      next: (data) => {
        this.surveyData = data;
        this.initializeSurvey(data);
      },
      error: (error) => {
        this.handleError(`File Error: ${error.message}`);
      }
    });
  }

  /**
   * Load survey data from JSON string input
   */
  private loadFromJSONString(): void {
    if (!this.jsonInput.trim()) {
      this.handleError('Please enter JSON data');
      return;
    }

    this.surveyPreviewService.loadSurveyFromJSONString(this.jsonInput.trim()).subscribe({
      next: (data) => {
        this.surveyData = data;
        this.initializeSurvey(data);
      },
      error: (error) => {
        this.handleError(`JSON Error: ${error.message}`);
      }
    });
  }

  /**
   * Load survey data from file path
   */
  private loadFromFilePath(): void {
    
    
    this.surveyPreviewService.loadSurveyFromFilePath(this.filePathInput.trim()).subscribe({
      next: (data) => {

        this.surveyData = data;
        this.initializeSurvey(data);
      },
      error: (error) => {

        // Load test data if file fails
        this.loadTestData();
      }
    });
  }

  /**
   * Load test data if file loading fails
   */
  private loadTestData(): void {

    const testData = {
      "Error": "NULL",
      "SurveyTitle": "Test Survey",
      "WelcomeText": "This is a test survey.",
      "Pages": [
        {
          "PageNo": "1",
          "PageName": "Test Page",
          "Questions": [
            {
              "QNo": "1",
              "QuestionType": "Text Box",
              "Title": "Enter your name:",
              "Answers": [{"ValueID": "1", "AnswerText": "", "Weight": "NULL"}]
            }
          ]
        }
      ],
      "ThankYouPage": "Thank you!",
      "SurveyLanguage": "English"
    };
    
    this.surveyData = testData;
    this.initializeSurvey(testData);
  }

  /**
   * Load survey data from component input
   */
  private loadFromInput(): void {
    if (this.surveyJsonData) {
      try {
        this.surveyData = JSON.parse(this.surveyJsonData);
        this.initializeSurvey(this.surveyData);
      } catch (e) {
        this.handleError('Error parsing survey JSON from input');
        this.loadDefaultData();
      }
    } else {
      this.loadDefaultData();
    }
  }

  /**
   * Initialize survey after data is loaded
   */
  private initializeSurvey(data: any): void {
    this.surveyData = data;
    
    // Convert AI survey data to Questions class format
    this.convertedQuestions = this.surveyPreviewService.convertToQuestionsClass(data);
    
    // Create Groups and Matrix data from grid questions
    this.createGroupsAndMatrixData();
    

    
    if (this.surveyData.Pages && this.surveyData.Pages.length > 0) {
      this.currentPageIndex = 0;
      this.currentPage = this.surveyData.Pages[0];
      this.showWelcome = true;
      this.showQuestions = false;
      this.showThankYou = false;
      this.isLoading = false;
      

    } else {

      this.handleError('Invalid survey data structure');
    }
  }

  /**
   * Create Groups and Matrix data from grid questions
   */
  private createGroupsAndMatrixData(): void {
    if (!this.surveyData) return;
    
    const groups: Group[] = [];
    const matrices: Matrix[] = [];
    let groupCounter = 1;
    let matrixCounter = 1;
    
    // Process each page and question to find grid questions
    if (this.surveyData.Pages) {
      this.surveyData.Pages.forEach((page, pageIndex) => {
        if (page.Questions) {
          page.Questions.forEach((question, qIndex) => {
                         // Check if this is a grid question, Multiple Text Box, Attachment, or Signature
             if (this.isGridQuestion(question.QuestionType) || 
                 question.QuestionType === 'Multiple Text Box' || 
                 question.QuestionType === 'Attachment' || 
                 question.QuestionType === 'Signature') {
              // Create a Group for this grid question
              const group = new Group();
              group.group_no = groupCounter;
              // For Multiple Text Box, use the question title as group title
              // For grid questions, use the question title as is
              group.gtitle = question.Title;
              group.PipeGTitle = '';
              group.QuestionTip = '';
              group.L_text = '';
              group.H_text = '';
              group.M_text = '';
              group.M_Pos = 0;
              group.Matrix_id = matrixCounter;
              group.colwidth = 0;
              group.isAnchorReverse = false;
              group.ShowDemoSubQno = false;
                             // Set group properties based on question type
               if (question.QuestionType === 'Multiple Text Box') {
                 group.answer_style = 0; // Default answer style for Multiple Text Box
                 group.isOverAllStyleApplied = true;
                 group.pipeTitle = '';
                 group.mgTextboxHeight = 1; // Text box height for Multiple Text Box
               } else if (question.QuestionType === 'Attachment') {
                 group.answer_style = 0; // Default answer style for Attachment
                 group.isOverAllStyleApplied = true;
                 group.pipeTitle = '';
                 group.mgTextboxHeight = 1; // Text box height for Attachment
               } else if (question.QuestionType === 'Signature') {
                 group.answer_style = 0; // Default answer style for Signature
                 group.isOverAllStyleApplied = true;
                 group.pipeTitle = '';
                 group.mgTextboxHeight = 1; // Text box height for Signature
               } else {
                 group.answer_style = -1; // Default answer style for grid questions
                 group.isOverAllStyleApplied = true;
                 group.pipeTitle = '';
                 group.mgTextboxHeight = 1;
               }
              group.IsScribbledSignature = false;
              group.IsUploadImageSignature = false;
              group.IsTypeSignature = false;
              
              groups.push(group);
              
              // Create a Matrix for this grid question
              const matrix = new Matrix();
              matrix.matrix_id = matrixCounter;
                             // For Multiple Text Box, Attachment, and Signature, use specific matrix titles
               // For grid questions, append " matrix" to the title
               if (question.QuestionType === 'Multiple Text Box') {
                 matrix.Mtitle = question.Title + " - Multiple Text Box Matrix";
               } else if (question.QuestionType === 'Attachment') {
                 matrix.Mtitle = question.Title + " - Attachment Matrix";
               } else if (question.QuestionType === 'Signature') {
                 matrix.Mtitle = question.Title + " - Signature Matrix";
               } else {
                 matrix.Mtitle = question.Title + " matrix";
               }
              matrix.PipeMTitle = '';
              matrix.questiontip = '';
              matrix.QPos = 0;
                             // Set matrix properties based on question type
               if (question.QuestionType === 'Multiple Text Box' || 
                   question.QuestionType === 'Attachment' || 
                   question.QuestionType === 'Signature') {
                 matrix.isMulti = true; // These question types have multiple sub-questions
                 matrix.MultiColumnCount = question.Rows ? question.Rows.length : 0;
                 matrix.subq_col_width = 0;
                 matrix.totalwidth = 0;
                 matrix.VerticalSpaceCount = 0;
                 matrix.IsSingleGrid = false; // These are not single grids
               } else {
                 matrix.isMulti = false;
                 matrix.MultiColumnCount = 0;
                 matrix.subq_col_width = 0;
                 matrix.totalwidth = 0;
                 matrix.VerticalSpaceCount = 0;
                 matrix.IsSingleGrid = true; // Grid questions are typically single grid
               }
              matrix.isBranchEnabled = false;
              matrix.SourceGrid = 0;
              matrix.SourcePosition = 0;
              matrix.SourceNumber = 0;
              matrix.TargetGrid = 0;
              matrix.isDisplayManage = false;
              matrix.isOverAllStyleApplied = true;
              matrix.isHiddenQ = false;
              
              matrices.push(matrix);
              
                             // Update the converted question to reference this group
               // For Multiple Text Box, Attachment, and Signature, we need to update both the main question and sub-questions
               if (question.QuestionType === 'Multiple Text Box' || 
                   question.QuestionType === 'Attachment' || 
                   question.QuestionType === 'Signature') {
                 // Find all questions with this qno (main question + sub-questions)
                 const relatedQuestions = this.convertedQuestions.filter(cq => cq.qno === parseInt(question.QNo));
                 relatedQuestions.forEach(cq => {
                   cq.group_no = groupCounter;
                 });
               } else {
                 // For grid questions, find the main question
                 const convertedQuestion = this.convertedQuestions.find(cq => cq.qno === parseInt(question.QNo));
                 if (convertedQuestion) {
                   convertedQuestion.group_no = groupCounter;
                 }
               }
              

              
              groupCounter++;
              matrixCounter++;
            }
          });
        }
      });
    }
    
    // Assign the created Groups and Matrix to the survey data
    this.surveyData.Groups = groups;
    this.surveyData.Matrix = matrices;
    

    

  }
  
  /**
   * Check if a question type is a grid question
   */
  private isGridQuestion(questionType: string): boolean {
    const gridTypes = [
      'Grid',
      'Matrix Grid',
      'Rating Matrix',
      'Rating Grid Matrix',
      'Grid Questions',
      'Grid Checkbox',
      'Single Response Grid',
      'Single Choice Grid',
      'Rating Grid'
    ];
    return gridTypes.includes(questionType);
  }
  
  /**
   * Get group title based on question type
   */
  private getGroupTitleFromQuestionType(questionType: string): string {
    switch (questionType) {
      case 'Grid':
        return 'Grid Radio';
      case 'Grid Checkbox':
        return 'Grid Checkbox';
      case 'Single Response Grid':
      case 'Single Choice Grid':
        return 'Single Choice Grid';
      case 'Rating Grid':
        return 'Rating Grid';
      case 'Matrix Grid':
      case 'Rating Matrix':
      case 'Rating Grid Matrix':
        return 'Matrix Grid';
      default:
        return 'Grid Question';
    }
  }
  
  /**
   * Get matrix title based on question type
   */
  private getMatrixTitleFromQuestionType(questionType: string): string {
    switch (questionType) {
      case 'Grid':
        return 'Radio Grid Matrix';
      case 'Grid Checkbox':
        return 'Checkbox Grid Matrix';
      case 'Single Response Grid':
      case 'Single Choice Grid':
        return 'Single Choice Grid Matrix';
      case 'Rating Grid':
        return 'Rating Grid Matrix';
      case 'Matrix Grid':
      case 'Rating Matrix':
      case 'Rating Grid Matrix':
        return 'Matrix Grid';
      default:
        return 'Grid Matrix';
    }
  }

  /**
   * Handle errors during data loading
   */
  private handleError(message: string): void {
    this.errorMessage = message;
    this.isLoading = false;
    this.loadDefaultData();
  }

  /**
   * Load default survey data as fallback
   */
  private loadDefaultData(): void {
    this.surveyData = this.surveyPreviewService.getDefaultSurveyData();
    this.initializeSurvey(this.surveyData);
  }

  /**
   * Handle file selection
   */
  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
      this.validateFile(file);
    }
  }

  /**
   * Validate selected file
   */
  private validateFile(file: File): void {
    this.isFileValid = false;
    this.fileValidationMessage = '';

    if (file.type === 'application/json' || file.name.endsWith('.json')) {
      this.isFileValid = true;
      this.fileValidationMessage = 'Valid JSON file selected';
    } else {
      this.fileValidationMessage = 'Please select a valid JSON file';
    }
  }

  /**
   * Clear file selection
   */
  clearFileSelection(): void {
    this.selectedFile = null;
    this.isFileValid = false;
    this.fileValidationMessage = '';
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  /**
   * Change data source
   */
  changeDataSource(source: 'api' | 'file' | 'json' | 'path'): void {
    this.dataSource = source;
    this.errorMessage = '';
    this.clearFileSelection();
    
    // Reset inputs based on source
    switch (source) {
      case 'api':
        this.apiUrl = '';
        this.corpno = '';
        break;
      case 'file':
        this.selectedFile = null;
        break;
      case 'json':
        this.jsonInput = '';
        break;
      case 'path':
        this.filePathInput = '';
        break;
    }
  }

  /**
   * Load data with current source
   */
  loadData(): void {
    this.loadSurveyData();
  }

  /**
   * Reset component to default state
   */
  resetToDefault(): void {
    this.dataSource = 'path';
    this.jsonInput = '';
    this.apiUrl = '';
    this.corpno = '';
    this.selectedFile = null;
    this.filePathInput = 'assets/ai_survey1.json';
    this.errorMessage = '';
    this.loadSurveyData();
  }

  /**
   * Start survey
   */
  startSurvey(): void {
    if (this.surveyData && this.surveyData.Pages && this.surveyData.Pages.length > 0) {
      this.showWelcome = false;
      this.showQuestions = true;
      this.currentPageIndex = 0;
      this.currentPage = this.surveyData.Pages[0];

    }
  }

  /**
   * Show debug information in console
   */
  showDebugInfo(): void {
    // Debug information method - can be expanded later if needed
  }

  nextPage(): void {
    if (this.currentPageIndex < this.surveyData!.Pages.length - 1) {
      this.currentPageIndex++;
      this.currentPage = this.surveyData!.Pages[this.currentPageIndex];
    } else {
      // Automatically show Thank You page when reaching the last page
      this.showThankYou = true;
      this.showQuestions = false;
      this.showWelcome = false;
    }
  }

  previousPage(): void {
    if (this.currentPageIndex > 0) {
      // Save current state before navigating back
      this.saveCurrentPageState();
      
      // Decrement page index
      this.currentPageIndex--;
      
      // Update current page
      if (this.surveyData && this.surveyData.Pages) {
        this.currentPage = this.surveyData.Pages[this.currentPageIndex];
      }
      
      // Reset any error states
      this.errorMessage = '';
      
      // Scroll to top of page (if needed)
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
      }
    }
  }

  private saveCurrentPageState(): void {
    // In preview mode, we don't need to save to server
    // but we can preserve form state in memory if needed
    // This method can be expanded later for state persistence
  }

  getProgressPercentage(): number {
    if (this.showWelcome) return 0;
    if (this.showThankYou) return 100;
    return ((this.currentPageIndex + 1) / this.surveyData!.Pages.length) * 100;
  }

  /**
   * Get converted question by QNo
   */
  getConvertedQuestion(qNo: string): Questions | undefined {
    const question = this.convertedQuestions.find(q => {
      const match = q.qno.toString() === qNo;
      return match;
    });
    
    return question;
  }

    /**
   * Get converted question as JSON string for PM control components
   */
  getConvertedQuestionJson(qNo: string): string {
    try {
      const question = this.getConvertedQuestion(qNo);
      if (question) {
        // For grid questions, we need to find all questions with the same qno (from the same grid)
        if (question.qtype === 'GR' || question.qtype === 'RW' || question.qtype === 'GC' || question.qtype === 'SC') {
          // Find all questions that belong to the same grid (same qno)
          const gridQuestions = this.convertedQuestions.filter(q => q.qno === question.qno);
          
          if (gridQuestions.length > 0) {
            // Sort by sub_qno to maintain order
            gridQuestions.sort((a, b) => (a.sub_qno || 0) - (b.sub_qno || 0));
            
            // Ensure all questions in the array have required properties
            const validatedGridQuestions = gridQuestions.map(q => {
              if (!q) return null;
              const validatedQuestion = { ...q };
              
              // Ensure required properties exist
              if (!validatedQuestion.qno) validatedQuestion.qno = parseInt(qNo) || 0;
              if (!validatedQuestion.qtitle) validatedQuestion.qtitle = question.qtitle || '';
              if (!validatedQuestion.qtype) validatedQuestion.qtype = question.qtype || 'GR';
              if (!validatedQuestion.qsubtype) validatedQuestion.qsubtype = question.qsubtype || 0;
              if (!validatedQuestion.Answers) validatedQuestion.Answers = [];
              if (!validatedQuestion.sub_qno) validatedQuestion.sub_qno = 0;
              if (!validatedQuestion.UniqueNumber) validatedQuestion.UniqueNumber = 0;
              validatedQuestion.group_no = question.group_no || 1;
              
              return validatedQuestion;
            }).filter(q => q !== null); // Remove any null questions
            
            if (validatedGridQuestions.length === 0) {
              return this.createFallbackGridQuestion(question);
            }
            
            const jsonString = JSON.stringify(validatedGridQuestions);
            
            // Validate the JSON data before returning
            if (!this.validateMatrixJson(jsonString)) {
              return this.createFallbackGridQuestion(question);
            }
            

            return jsonString;
          }
        }
        
        // Also check if the question type is null/undefined but the question type description suggests it's a matrix
        if (!question.qtype && question.qtitle && (
          question.qtitle.toLowerCase().includes('grid') || 
          question.qtitle.toLowerCase().includes('matrix')
        )) {
          const matrixQuestions = this.createMatrixQuestions(question);
          
          // Validate matrix questions
          const validatedMatrixQuestions = matrixQuestions.map(q => {
            if (!q) return null;
            const validatedQuestion = { ...q };
            if (!validatedQuestion.qno) validatedQuestion.qno = parseInt(qNo) || 0;
            if (!validatedQuestion.qtitle) validatedQuestion.qtitle = question.qtitle || '';
            if (!validatedQuestion.qtype) validatedQuestion.qtype = question.qtype || 'GR';
            if (!validatedQuestion.qsubtype) validatedQuestion.qsubtype = question.qsubtype || 0;
            if (!validatedQuestion.Answers) validatedQuestion.Answers = [];
            if (!validatedQuestion.sub_qno) validatedQuestion.sub_qno = 0;
            if (!validatedQuestion.UniqueNumber) validatedQuestion.UniqueNumber = 0;
            validatedQuestion.group_no = 1;
            return validatedQuestion;
          }).filter(q => q !== null);
          
          if (validatedMatrixQuestions.length === 0) {
            return this.createFallbackMatrixQuestion(question);
          }
          
          const jsonString = JSON.stringify(validatedMatrixQuestions);
          
          // Validate the JSON data before returning
          if (!this.validateMatrixJson(jsonString)) {
            return this.createFallbackMatrixQuestion(question);
          }
          

          return jsonString;
        }
        
                 // Handle multiple textbox, attachment, and signature questions
         if (question.qtype === 'T' && (question.qsubtype === 71 || question.qsubtype === 72 || (question.qsubtype === 0 && question.group_no > 0))) {
           // For these question types, we need to create a structure similar to grid questions
           // but with the specific properties for each type
           const specialQuestions = this.createSpecialTextQuestions(question);
          
                     if (specialQuestions.length > 0) {
             const jsonString = JSON.stringify(specialQuestions);
             
             // Validate the JSON data before returning
             if (!this.validateMatrixJson(jsonString)) {
               return this.createFallbackSpecialTextQuestion(question);
             }
             
             return jsonString;
           } else {
             return this.createFallbackSpecialTextQuestion(question);
           }
        }
        
        // Handle regular text questions (single text box)
        if (question.qtype === 'T' && question.qsubtype === 0 && (!question.group_no || question.group_no === 0)) {
          // Single text box - return as single object
          const safeQuestion = { ...question };
          
          // Handle any potentially undefined properties
          Object.keys(safeQuestion).forEach(key => {
            if (safeQuestion[key] === undefined) {
              if (typeof safeQuestion[key] === 'boolean') {
                safeQuestion[key] = false;
              } else if (typeof safeQuestion[key] === 'number') {
                safeQuestion[key] = 0;
              } else if (typeof safeQuestion[key] === 'string') {
                safeQuestion[key] = '';
              } else if (Array.isArray(safeQuestion[key])) {
                safeQuestion[key] = [];
              } else {
                safeQuestion[key] = null;
              }
            }
          });
          
          const result = JSON.stringify(safeQuestion);
          return result;
        }
        
        // For regular single questions, determine if they should return array or single object format
        const safeQuestion = { ...question };
        
        // Handle any potentially undefined properties
        Object.keys(safeQuestion).forEach(key => {
          if (safeQuestion[key] === undefined) {
            if (typeof safeQuestion[key] === 'boolean') {
              safeQuestion[key] = false;
            } else if (typeof safeQuestion[key] === 'number') {
              safeQuestion[key] = 0;
            } else if (typeof safeQuestion[key] === 'string') {
              safeQuestion[key] = '';
            } else if (Array.isArray(safeQuestion[key])) {
              safeQuestion[key] = [];
            } else {
              safeQuestion[key] = null;
            }
          }
        });
        
        // Check if this question type should return array format
        if (this.shouldReturnArrayFormat(safeQuestion.qtype)) {
          // Return as array for grid/matrix questions
          const questionsArray = [safeQuestion];
          const result = JSON.stringify(questionsArray);
          return result;
        } else {
          // Return single question object for regular single questions
          const result = JSON.stringify(safeQuestion);
          return result;
        }
      }

      return '{}';
    } catch (error) {
      return '{}';
    }
  }

  /**
   * Check if a question type should return array format or single object format
   */
  private shouldReturnArrayFormat(questionType: string): boolean {
    // These question types expect array format (multiple questions)
    const arrayFormatTypes = [
      'GR', 'RW', 'GC', 'SC' // Grid/Matrix types only
    ];
    
    // RT (Rating Radio Button) should be treated as single question, not array
    if (questionType === 'RT') {
      return false;
    }
    
    const shouldReturnArray = arrayFormatTypes.includes(questionType);
    return shouldReturnArray;
  }

  /**
   * Validate JSON data for matrix-grid component
   */
  private validateMatrixJson(jsonData: string): boolean {
    try {
      const parsed = JSON.parse(jsonData);
      if (!Array.isArray(parsed)) {
        return false;
      }
      
      if (parsed.length === 0) {
        return false;
      }
      
      // Check that each question has required properties
      for (let i = 0; i < parsed.length; i++) {
        const question = parsed[i];
        if (!question || typeof question !== 'object') {
          return false;
        }
        
        if (question.qno === undefined || question.qno === null) {
          return false;
        }
        
        if (question.qtitle === null || question.qtitle === undefined) {
          return false;
        }
      }
      return true;
    } catch (error) {

      return false;
    }
  }

  /**
   * Get multi questions string for grid questions
   * Based on the participation component's GetMultiQuestionsString method
   */
  private getMultiQuestionsString(question: Questions): string {
    // Filter questions by qno and group_no (like GetMultiQuestionsString does)
    if (this.surveyData?.Pages && this.currentPageIndex >= 0) {
      const currentPage = this.surveyData.Pages[this.currentPageIndex];
      if (currentPage?.Questions) {
        const multiQuestions = currentPage.Questions.filter(
          (e) => e.QNo === question.qno.toString() && e.group_no === question.group_no
        );
        
        if (multiQuestions && multiQuestions.length > 0) {
          // Convert SurveyQuestion to Questions type (similar to participation component)
          const convertedQuestions = multiQuestions.map((sq, index) => {
            const convertedQuestion = new Questions();
            
            // Map basic properties
            convertedQuestion.qno = parseInt(sq.QNo);
            convertedQuestion.qtitle = sq.Title;
            convertedQuestion.qtype = sq.QuestionType;
            convertedQuestion.qsubtype = parseInt(sq.SubQuestionType || '0');
            convertedQuestion.group_no = sq.group_no || 0;
            convertedQuestion.UniqueNumber = parseFloat((sq as any).UniqueNumber?.toString() || '0');
            convertedQuestion.sub_qno = parseFloat((sq as any).sub_qno?.toString() || '0');
            convertedQuestion.mandatory = (sq as any).mandatory || false;
            convertedQuestion.AnswerText = (sq as any).AnswerText;
            
            // Convert answers if they exist
            if (sq.Answers) {
              convertedQuestion.Answers = sq.Answers.map(ans => {
                const answer = new Answers();
                answer.value_id = parseInt(ans.ValueID);
                answer.value_text = ans.AnswerText;
                answer.weight = parseFloat(ans.Weight || '0');
                return answer;
              });
            } else {
              convertedQuestion.Answers = [];
            }
            
            // Set multi-question properties (like in participation component)
            if (convertedQuestion.UniqueNumber !== question.UniqueNumber) {
              convertedQuestion.isMultiQuestion = true;
            }
            
            // Handle mandatory upfront setting
            if (this.surveyData?.SurveySettings?.isMandatoryUpfront) {
              convertedQuestion.isMandatoryStar = true;
            }
            
            // Handle character limit
            if (convertedQuestion.Showcharlimit) {
              convertedQuestion.RemainingChr = this.surveyMessages.RemainingChr || 'Characters Remaining';
            }
            
            // Set default validation state
            convertedQuestion.isValid = false;
            
            return convertedQuestion;
          });
          
          return JSON.stringify(convertedQuestions);
        }
      }
    }
    
    return '{}';
  }

  /**
   * Create grid questions structure for regular grid questions
   * Based on the participation component's GetMultiQuestionsString method
   */
  private createGridQuestions(mainQuestion: Questions): Questions[] {
    const gridQuestions: Questions[] = [];
    

    
    // For grid questions, we need to find questions with the same qno and group_no
    if (this.surveyData?.Pages && this.currentPageIndex >= 0) {
      const currentPage = this.surveyData.Pages[this.currentPageIndex];
      if (currentPage?.Questions) {
        // Filter questions by qno and group_no (like GetMultiQuestionsString does)
        const filteredQuestions = currentPage.Questions.filter(
          (e) => e.QNo === mainQuestion.qno.toString() && e.group_no === mainQuestion.group_no
        );
        
        if (filteredQuestions.length > 0) {
          // Convert SurveyQuestion to Questions type
          for (let i = 0; i < filteredQuestions.length; i++) {
            const sq = filteredQuestions[i];
            const question = new Questions();
            
            // Map basic properties
            question.qno = parseInt(sq.QNo);
            question.qtitle = sq.Title;
            question.qtype = sq.QuestionType;
            question.qsubtype = parseInt(sq.SubQuestionType || '0');
            question.group_no = sq.group_no || 0;
            question.UniqueNumber = mainQuestion.UniqueNumber;
            
            // Convert answers if they exist
            if (sq.Answers) {
              question.Answers = sq.Answers.map(ans => {
                const answer = new Answers();
                answer.value_id = parseInt(ans.ValueID);
                answer.value_text = ans.AnswerText;
                answer.weight = parseFloat(ans.Weight || '0');
                return answer;
              });
            } else {
              question.Answers = [];
            }
            
            // Set grid question properties
            if (i > 0) {
              question.isMultiQuestion = true;
            }
            
            // Handle mandatory upfront setting
              question.isMandatoryStar = true;

            
            // Handle character limit
            if (question.Showcharlimit) {
              question.RemainingChr = this.surveyMessages.RemainingChr || 'Characters Remaining';
            }
            
            // Set default validation state
            question.isValid = false;
            
            gridQuestions.push(question);
          }
        }
      }
    }
    
    // If no grid structure found, create a basic structure as fallback
    if (gridQuestions.length === 0) {
      // Ensure mainQuestion has required properties
      const safeMainQuestion = { ...mainQuestion };
      if (!safeMainQuestion.qno) safeMainQuestion.qno = 0;
      if (!safeMainQuestion.qtitle) safeMainQuestion.qtitle = 'Grid Question';
      if (!safeMainQuestion.qtype) safeMainQuestion.qtype = 'GR';
      if (!safeMainQuestion.qsubtype) safeMainQuestion.qsubtype = 0;
      if (!safeMainQuestion.Answers) safeMainQuestion.Answers = [];
      if (!safeMainQuestion.sub_qno) safeMainQuestion.sub_qno = 0;
      if (!safeMainQuestion.UniqueNumber) safeMainQuestion.UniqueNumber = 0;
      
      const subQuestion = { ...safeMainQuestion };
      
      // Set sub-question properties
      subQuestion.sub_qno = 1;
      subQuestion.isMultiQuestion = true;
      
      // Add the main question and sub-question to the array
      gridQuestions.push(safeMainQuestion);
      gridQuestions.push(subQuestion);
    }
    return gridQuestions;
  }

  /**
   * Create a fallback grid question when the main method fails
   */
  private createFallbackGridQuestion(mainQuestion: Questions): string {
    
    // Create a basic grid structure with the main question and a sub-question
    const fallbackQuestions = [
      {
        ...mainQuestion,
        qno: mainQuestion.qno || 0,
        qtitle: mainQuestion.qtitle || 'Grid Question',
        qtype: mainQuestion.qtype || 'GR',
        qsubtype: mainQuestion.qsubtype || 0,
        sub_qno: 0,
        UniqueNumber: mainQuestion.UniqueNumber || 0,
        Answers: mainQuestion.Answers || [],
        isMultiQuestion: false,
        isMandatoryStar: false
      },
      {
        ...mainQuestion,
        qno: mainQuestion.qno || 0,
        qtitle: mainQuestion.qtitle || 'Grid Question',
        qtype: mainQuestion.qtype || 'GR',
        qsubtype: mainQuestion.qsubtype || 0,
        sub_qno: 1,
        UniqueNumber: mainQuestion.UniqueNumber || 0,
        Answers: mainQuestion.Answers || [],
        isMultiQuestion: true,
        isMandatoryStar: false
      }
    ];
    
    const result = JSON.stringify(fallbackQuestions);
    return result;
  }

  /**
   * Create special text questions (multiple textbox, attachment, signature)
   * Based on SMQType.json structure and ParticipationData.json examples
   * 
   * Normal Textbox: qtype = "T", qsubtype = 0, group_no = 0
   * Multiple Text Box (MTB): qtype = "T", qsubtype = 0, group_no > 0
   * Attachment: qtype = "T", qsubtype = 71
   * Signature: qtype = "T", qsubtype = 72
   */
     private createSpecialTextQuestions(mainQuestion: Questions): Questions[] {

     const specialQuestions: Questions[] = [];
    
                   if (mainQuestion.qsubtype === 71) {
        // Attachment question (qsubtype = 71) - only include sub-questions (no main question)
        // For Attachment questions, we need to get the original question data to access Rows
        const originalQuestion = this.getOriginalQuestionData(mainQuestion.qno);
        
        if (originalQuestion && originalQuestion.Rows && originalQuestion.Rows.length > 0) {

          
          // Create sub-questions for each row using the row labels
          // Only create sub-questions for the actual rows (not more than what exists)
          const actualRows = originalQuestion.Rows.slice(0, originalQuestion.Rows.length);
          
          actualRows.forEach((row: any, rowIndex: number) => {
            const additionalAttachmentQ = { ...mainQuestion };
            additionalAttachmentQ.sub_qno = rowIndex + 1;
            additionalAttachmentQ.isMultiQuestion = true;
            additionalAttachmentQ.isMandatoryStar = false;
            // Use the row label as the sub-question title
            additionalAttachmentQ.qtitle = row.Label || `Question ${row.ID}`;
            (additionalAttachmentQ as any).isAttachment = true;
            (additionalAttachmentQ as any).isSignature = false;
            additionalAttachmentQ.UniqueNumber = mainQuestion.UniqueNumber || 0;
            specialQuestions.push(additionalAttachmentQ);
            

          });
          

        } else {
          // Fallback if no row data available
          
          // Create sub-questions based on available data or use a reasonable default
          // The count can be dynamic based on the original question structure
          const subQuestionCount = originalQuestion && originalQuestion.Rows ? originalQuestion.Rows.length : 2;
          for (let i = 1; i <= subQuestionCount; i++) {
            const additionalAttachmentQ = { ...mainQuestion };
            additionalAttachmentQ.sub_qno = i;
            additionalAttachmentQ.isMultiQuestion = true;
            additionalAttachmentQ.isMandatoryStar = false;
            // Try to use row label if available, otherwise use generic title
            const rowLabel = originalQuestion && originalQuestion.Rows && originalQuestion.Rows[i-1] ? 
              originalQuestion.Rows[i-1].Label : `${mainQuestion.qtitle || 'Attachment'} ${i}`;
            additionalAttachmentQ.qtitle = rowLabel;
            (additionalAttachmentQ as any).isAttachment = true;
            (additionalAttachmentQ as any).isSignature = false;
            additionalAttachmentQ.UniqueNumber = mainQuestion.UniqueNumber || 0;
            specialQuestions.push(additionalAttachmentQ);
          }
          

        }
         } else if (mainQuestion.qsubtype === 72) {
       // Signature question (qsubtype = 72) - only include sub-questions (no main question)
       
       // For Signature questions, we need to get the original question data to access Rows
       const originalQuestion = this.getOriginalQuestionData(mainQuestion.qno);
       
       if (originalQuestion && originalQuestion.Rows && originalQuestion.Rows.length > 0) {

         
         // Create sub-questions for each row using the row labels
         // Only create sub-questions for the actual rows (not more than what exists)
         const actualRows = originalQuestion.Rows.slice(0, originalQuestion.Rows.length);
         
         actualRows.forEach((row: any, rowIndex: number) => {
           const additionalSignatureQ = { ...mainQuestion };
           additionalSignatureQ.sub_qno = rowIndex + 1;
           additionalSignatureQ.isMultiQuestion = true;
           additionalSignatureQ.isMandatoryStar = false;
           // Use the row label as the sub-question title
           additionalSignatureQ.qtitle = row.Label || `Question ${row.ID}`;
           (additionalSignatureQ as any).isAttachment = false;
           (additionalSignatureQ as any).isSignature = true;
           additionalSignatureQ.UniqueNumber = mainQuestion.UniqueNumber || 0;
           specialQuestions.push(additionalSignatureQ);
           

         });
         

       } else {
         // Fallback if no row data available
         
         // Create sub-questions based on available data or use a reasonable default
         // For signature questions without rows, create at least 2 sub-questions for better UX
         const subQuestionCount = originalQuestion && originalQuestion.Rows ? originalQuestion.Rows.length : 2;
         for (let i = 1; i <= subQuestionCount; i++) {
           const additionalSignatureQ = { ...mainQuestion };
           additionalSignatureQ.sub_qno = i;
           additionalSignatureQ.isMultiQuestion = true;
           additionalSignatureQ.isMandatoryStar = false;
           // Try to use row label if available, otherwise use generic title
           const rowLabel = originalQuestion && originalQuestion.Rows && originalQuestion.Rows[i-1] ? 
             originalQuestion.Rows[i-1].Label : `${mainQuestion.qtitle || 'Signature'} ${i}`;
           additionalSignatureQ.qtitle = rowLabel;
           (additionalSignatureQ as any).isAttachment = false;
           (additionalSignatureQ as any).isSignature = true;
           additionalSignatureQ.UniqueNumber = mainQuestion.UniqueNumber || 0;
           specialQuestions.push(additionalSignatureQ);
         }
         

       }
     } else if (mainQuestion.qsubtype === 0 && mainQuestion.group_no > 0) {
       // Multiple Text Box (MTB) - qsubtype = 0, group_no > 0
       // For Multiple Text Box, we need to get the original question data to access Rows
       const originalQuestion = this.getOriginalQuestionData(mainQuestion.qno);
       
               if (originalQuestion && originalQuestion.Rows && originalQuestion.Rows.length > 0) {

          
          // Create sub-questions for each row using the row labels
          // Only create sub-questions for the actual rows (not more than what exists)
          const actualRows = originalQuestion.Rows.slice(0, originalQuestion.Rows.length);
          
          actualRows.forEach((row: any, rowIndex: number) => {
            const additionalTextBoxQ = { ...mainQuestion };
            additionalTextBoxQ.sub_qno = rowIndex + 1;
            additionalTextBoxQ.isMultiQuestion = true;
            additionalTextBoxQ.isMandatoryStar = false;
            // Use the row label as the sub-question title
            additionalTextBoxQ.qtitle = row.Label || `Question ${row.ID}`;
            (additionalTextBoxQ as any).isAttachment = false;
            (additionalTextBoxQ as any).isSignature = false;
            additionalTextBoxQ.UniqueNumber = mainQuestion.UniqueNumber || 0;
            specialQuestions.push(additionalTextBoxQ);
            

          });
          

               } else {
          // Fallback if no row data available
          
          // Create sub-questions based on available data or use a reasonable default
          // The count can be dynamic based on the original question structure
          const subQuestionCount = originalQuestion.Rows ? originalQuestion.Rows.length : 3;
          for (let i = 1; i <= subQuestionCount; i++) {
            const additionalTextBoxQ = { ...mainQuestion };
            additionalTextBoxQ.sub_qno = i;
            additionalTextBoxQ.isMultiQuestion = true;
            additionalTextBoxQ.isMandatoryStar = false;
            // Try to use row label if available, otherwise use generic title
            const rowLabel = originalQuestion.Rows && originalQuestion.Rows[i-1] ? 
              originalQuestion.Rows[i-1].Label : `${mainQuestion.qtitle || 'Multiple Text Box'} ${i}`;
            additionalTextBoxQ.qtitle = rowLabel;
            (additionalTextBoxQ as any).isAttachment = false;
            (additionalTextBoxQ as any).isSignature = false;
            additionalTextBoxQ.UniqueNumber = mainQuestion.UniqueNumber || 0;
            specialQuestions.push(additionalTextBoxQ);
          }
          

        }
         }
     

     
     return specialQuestions;
   }

  /**
   * Create a fallback special text question when the main method fails
   */
     private createFallbackSpecialTextQuestion(mainQuestion: Questions): string {
     const fallbackQuestions: any[] = [];
     
                 // For Multiple Text Box and Attachment, only include sub-questions (no main question)
      if (mainQuestion.qsubtype === 0) {
        // Try to get the original question data to determine the actual sub-question count
        const originalQuestion = this.getOriginalQuestionData(mainQuestion.qno);
        const subQuestionCount = originalQuestion && originalQuestion.Rows ? originalQuestion.Rows.length : 3;
        
        for (let i = 1; i <= subQuestionCount; i++) {
          // Try to use row label if available, otherwise use generic title
          const rowLabel = originalQuestion && originalQuestion.Rows && originalQuestion.Rows[i-1] ? 
            originalQuestion.Rows[i-1].Label : `${mainQuestion.qtitle || 'Multiple Text Box'} ${i}`;
          
          fallbackQuestions.push({
            ...mainQuestion,
            qno: mainQuestion.qno || 0,
            qtitle: rowLabel,
            qtype: mainQuestion.qtype || 'T',
            qsubtype: mainQuestion.qsubtype || 0,
            sub_qno: i,
            UniqueNumber: mainQuestion.UniqueNumber || 0,
            Answers: mainQuestion.Answers || [],
            isMultiQuestion: true,
            isMandatoryStar: false,
            isAttachment: false,
            isSignature: false
          });
        }
        
        
                           } else if (mainQuestion.qsubtype === 71) {
                     // For Attachment questions, only include sub-questions (no main question)
           // Try to get the original question data to determine the actual sub-question count
           const originalQuestion = this.getOriginalQuestionData(mainQuestion.qno);
           const subQuestionCount = originalQuestion && originalQuestion.Rows ? originalQuestion.Rows.length : 2;
          
          for (let i = 1; i <= subQuestionCount; i++) {
            // Try to use row label if available, otherwise use generic title
            const rowLabel = originalQuestion && originalQuestion.Rows && originalQuestion.Rows[i-1] ? 
              originalQuestion.Rows[i-1].Label : `${mainQuestion.qtitle || 'Attachment'} ${i}`;
            
            fallbackQuestions.push({
              ...mainQuestion,
              qno: mainQuestion.qno || 0,
              qtitle: rowLabel,
              qtype: mainQuestion.qtype || 'T',
              qsubtype: mainQuestion.qsubtype || 71,
              sub_qno: i,
              UniqueNumber: mainQuestion.UniqueNumber || 0,
              Answers: mainQuestion.Answers || [],
              isMultiQuestion: true,
              isMandatoryStar: false,
              isAttachment: true,
              isSignature: false
            });
          }
          

       } else if (mainQuestion.qsubtype === 72) {
                   // For Signature questions, only include sub-questions (no main question)
          const subQuestionCount = 2;
         
         for (let i = 1; i <= subQuestionCount; i++) {
           fallbackQuestions.push({
             ...mainQuestion,
             qno: mainQuestion.qno || 0,
             qtitle: `${mainQuestion.qtitle || 'Signature'} ${i}`,
             qtype: mainQuestion.qtype || 'T',
             qsubtype: mainQuestion.qsubtype || 72,
             sub_qno: i,
             UniqueNumber: mainQuestion.UniqueNumber || 0,
             Answers: mainQuestion.Answers || [],
             isMultiQuestion: true,
             isMandatoryStar: false,
             isAttachment: false,
             isSignature: true
           });
         }
         

       } else {
         // For other question types, include the main question
         fallbackQuestions.push({
           ...mainQuestion,
           qno: mainQuestion.qno || 0,
           qtitle: mainQuestion.qtitle || 'Special Text Question',
           qtype: mainQuestion.qtype || 'T',
           qsubtype: mainQuestion.qsubtype || 0,
           sub_qno: 0,
           UniqueNumber: mainQuestion.UniqueNumber || 0,
           Answers: mainQuestion.Answers || [],
           isMultiQuestion: false,
           isMandatoryStar: false,
           isAttachment: mainQuestion.qsubtype === 71,
           isSignature: mainQuestion.qsubtype === 72
         });
       }
    
    const result = JSON.stringify(fallbackQuestions);
    return result;
  }





  /**
   * Create a fallback matrix question when the main method fails
   */
  private createFallbackMatrixQuestion(mainQuestion: Questions): string {
    
    // Create a basic matrix structure with the main question and a sub-question
    const fallbackQuestions = [
      {
        ...mainQuestion,
        qno: mainQuestion.qno || 0,
        qtitle: mainQuestion.qtitle || 'Matrix Question',
        qtype: mainQuestion.qtype || 'GR',
        qsubtype: mainQuestion.qsubtype || 0,
        sub_qno: 0,
        UniqueNumber: mainQuestion.UniqueNumber || 0,
        Answers: mainQuestion.Answers || [],
        isMultiQuestion: false,
        isMandatoryStar: false
      },
      {
        ...mainQuestion,
        qno: mainQuestion.qno || 0,
        qtitle: mainQuestion.qtitle || 'Matrix Question',
        qtype: mainQuestion.qtype || 'GR',
        qsubtype: mainQuestion.qsubtype || 0,
        sub_qno: 1,
        UniqueNumber: mainQuestion.UniqueNumber || 0,
        Answers: mainQuestion.Answers || [],
        isMultiQuestion: true,
        isMandatoryStar: false
      }
    ];
    
    const result = JSON.stringify(fallbackQuestions);
    return result;
  }

  /**
   * Create matrix questions structure for grid/matrix questions
   * Based on the participation component's GetMatrixGridQuestionsString method
   * and the ParticipationData.json structure
   */
  private createMatrixQuestions(mainQuestion: Questions): Questions[] {
    const matrixQuestions: Questions[] = [];
    
    // Get group data for the question
    const groupData = this.getGroupDataByNumber(mainQuestion.group_no);
    if (groupData) {
      // Get matrix data for the group
      const matrixData = this.getMatrixDataForGroup(groupData);
      if (matrixData) {
        // Get all groups for this matrix
        const groupDatas = this.getGroupDatas(matrixData);
        
        if (groupDatas && groupDatas.length > 0) {
          for (const group of groupDatas) {
            // Get questions for this group from the current page
            const questionDatas = this.getQuestionsForGroup(group.group_no);
            
                         if (questionDatas.length > 0) {
               for (let questionData of questionDatas) {
                 // Ensure questionData is valid
                 if (!questionData || typeof questionData !== 'object') {
                   continue;
                 }
                 
                 // Create a safe copy of the question data
                 const safeQuestionData = { ...questionData };
                 
                 // Ensure required properties exist
                 if (!safeQuestionData.qno) safeQuestionData.qno = mainQuestion.qno || 0;
                 if (!safeQuestionData.qtitle) safeQuestionData.qtitle = mainQuestion.qtitle || '';
                 if (!safeQuestionData.qtype) safeQuestionData.qtype = mainQuestion.qtype || 'GR';
                 if (!safeQuestionData.qsubtype) safeQuestionData.qsubtype = mainQuestion.qsubtype || 0;
                 if (!safeQuestionData.Answers) safeQuestionData.Answers = [];
                 if (!safeQuestionData.sub_qno) safeQuestionData.sub_qno = 0;
                 if (!safeQuestionData.UniqueNumber) safeQuestionData.UniqueNumber = mainQuestion.UniqueNumber || 0;
                 
                 // Set matrix question properties
                 if (mainQuestion.UniqueNumber !== safeQuestionData.UniqueNumber) {
                   safeQuestionData.isMultiQuestion = true;
                 }
                 
                 // Handle mandatory upfront setting
                 if (this.surveyData?.SurveySettings?.isMandatoryUpfront) {
                   safeQuestionData.isMandatoryStar = true;
                 }
                 
                 // Handle answer sorting for specific question types
                 if (mainQuestion.qtype === 'RG' && group.isAnchorReverse) {
                   if (this.isMobile) {
                     if (!(safeQuestionData.qsubtype === 1 && this.isRTL)) {
                       safeQuestionData.Answers.sort((n1, n2) => n2.weight - n1.weight);
                     }
                   } else {
                     safeQuestionData.Answers.sort((n1, n2) => n2.weight - n1.weight);
                   }
                 } else if (
                   (safeQuestionData.qtype === 'GR' || safeQuestionData.qtype === 'SR') &&
                   (safeQuestionData.qsubtype === 1 ||
                     (safeQuestionData.qsubtype === 2 && safeQuestionData.qtype === 'GR')) &&
                   group.isAnchorReverse
                 ) {
                   safeQuestionData.Answers.sort((n1, n2) => n2.value_id - n1.value_id);
                 }
                 
                 // Handle character limit
                 if (safeQuestionData.Showcharlimit) {
                   safeQuestionData.RemainingChr = this.surveyMessages.RemainingChr || 'Characters Remaining';
                 }
                 
                 matrixQuestions.push(safeQuestionData);
               }
             }
          }
        }
      }
    }
    
    // If no matrix structure found, create a basic structure as fallback
    if (matrixQuestions.length === 0) {
      
      // Ensure mainQuestion has required properties
      const safeMainQuestion = { ...mainQuestion };
      if (!safeMainQuestion.qno) safeMainQuestion.qno = 0;
      if (!safeMainQuestion.qtitle) safeMainQuestion.qtitle = 'Matrix Question';
      if (!safeMainQuestion.qtype) safeMainQuestion.qtype = 'GR';
      if (!safeMainQuestion.qsubtype) safeMainQuestion.qsubtype = 0;
      if (!safeMainQuestion.Answers) safeMainQuestion.Answers = [];
      if (!safeMainQuestion.sub_qno) safeMainQuestion.sub_qno = 0;
      if (!safeMainQuestion.UniqueNumber) safeMainQuestion.UniqueNumber = 0;
      
      const subQuestion = { ...safeMainQuestion };
      
      // Set sub-question properties
      subQuestion.sub_qno = 1;
      subQuestion.isMultiQuestion = true;
      
      // Add the main question and sub-question to the array
      matrixQuestions.push(safeMainQuestion);
      matrixQuestions.push(subQuestion);
    }
    

    return matrixQuestions;
  }

  /**
   * Update survey messages with new data
   */
  updateSurveyMessages(messages: Partial<SurveyMessages>): void {
    this.surveyPreviewService.updateSurveyMessages(messages);
    this.surveyMessages = this.surveyPreviewService.getSurveyMessages();
  }

  /**
   * Get a specific survey message
   */
  getSurveyMessage(key: keyof SurveyMessages): string | string[] | null {
    return this.surveyPreviewService.getSurveyMessage(key);
  }

  /**
   * Load the default survey messages (this demonstrates how to use the JSON data)
   */
  loadDefaultSurveyMessages(): void {
    // This is the JSON data you provided
    const defaultMessages: Partial<SurveyMessages> = {
      NextBtnText: null,
      next: "Nextsgs",
      back: "Backgsgsg",
      submit: "Submitsgsg",
      Continue1: "Continuesgsgsg",
      SaveContinueLater: "Save & continue later",
      ClearAllAnswers: "Clear answers on page",
      AsteriskMandatory: "Questions with an asterisk (<span class='icon__mandatory'></span>) are mandatory. Please answer all mandatory questions to proceed.",
      SurveyTimeOutMessage: "As a security measure, your survey timeout is set to 60 minutes of inactivity. If you leave this survey inactive for this duration, all your previous answers may be lost.",
      page: "Page",
      of1: "of",
      requiredInfo: "Required Information",
      ResponseRecorded: "Your response has been recorded.",
      AnonymousHdr: " Anonymous Survey",
      MobileAnonymousHdr: " Anonymous Survey",
      StrSaveHelpHeader: "Save & continue later",
      StrSaveHelp: "<p class=\"no-margin\">The feature \"Save & continue later\" allows respondents to save partially completed surveys and return later.</p> \r\n<p class=\"no-margin\">Respondents can access the saved survey by clicking the shared link from the save and continue later page to either</p><p class=\"no-margin\"> 1) Review answers already entered or</p> \r\n<p class=\"no-margin\">2) Continue taking the survey from the last session.</p>",
      RemainingChr: "Characters Remaining",
      ReEnterBlankMessage: "Please re-enter the email address.",
      ThankyouPage: null,
      PrintBlankSurvey: "Print Blank Survey",
      SurveyTimeOutWarning1: "It appears that you have been on this page for an extended period with no activity. If you require additional time, please click \"OK\". Failure to do so will result in loss of your responses.",
      SurveyTimeOutWarning2: "For security reasons your online survey session has been timed out due to inactivity. You can click on the participation link again to complete this survey.",
      accessedTestMode: "You have accessed this Survey in TEST MODE. Your responses will not be recorded in reports.",
      Rank: "Rank",
      DropDown: "--Select--",
      AnonymousMessage: "[{\"header\":\"Default Message\",\"desc\":\"Your responses are absolutely anonymous to the sender of this survey.\"},{\"header\":\"What does that mean?\",\"desc\":\"The sender will see your answers, but they won't be able to identify them with you personally. \"},{\"header\":\"How is that possible?\",\"desc\":\"The platform enables data collection while blocking the administrator from connecting your response with your email address, password, or IP address.\"},{\"header\":\"I received a reminder! So…?\",\"desc\":\"Nobody knows that you receive a reminder unless you tell them! In an anonymous survey, even the sender doesn't know who receives reminders.\"},{\"header\":\"Why this anonymity feature?\",\"desc\":\"We know that participants want to share honest answers but can sometimes feel that telling the whole truth is too risky. We also want to make sure that administrators can collect useful data and protect your privacy. The result? Accurate and useful reporting data and complete anonymity for you.\"}]",
      AnonymousMessageMobile: "<div style=\"padding: 14px;\">This is a Sogolytics Anonymous Survey.<br>Sogolytics's innovative technology makes it possible for you to participate in this survey and not have your responses associate with your email address or login.<br>For more information regarding Sogolytics's anonymous surveys, <click_here><br><a href=https://sogolyticsintuc.sevenpv.com/static/anonymouspage.aspx?lang=0&type=Sogolytics&isPoweredByClient=True&isAssessment=False target='_blank'>click here.</a></click_here></div>",
      AnonymousMessageHeader: " Anonymous Survey",
      Message: "Message",
      QuotaExaustMsg: "Quota limits for all answer options have been met. No response to this question can be submitted at this time. Please continue to the next page.",
      ParticipationQuotaExaust: "Your previous answer selected for this question has met its required quota. Please select another answer option.",
      AreUsureClear: "Are you sure you want to clear all the answers on this page? ",
      TestWarningMsg: "You have participated in a test version of this project. Your responses will not be included in reports.",
      TestHeader: "TEST MODE",
      TestGotIt: "Got it​",
      strOk: "OK",
      strWarning: "Warning:",
      Search: "Search",
      strinvalidvaluemsg: "Invalid Value! Please enter number.",
      Clear: "Clear",
      strCookieDisable: " Dear Survey Participant,<br /> <br />\r\n               It appears that your current browser settings are preventing you from viewing this Survey. <br />\r\n               <br /> Please try these steps to make this Survey available:\r\n                  <ul>\r\n                     <li> From the Settings menu, select Internet Options </li>\r\n                     <li> On the Security Page, set the Security Level to Medium </li>\r\n                     <li> On the Privacy Page, set the Privacy Level to Low. </li>\r\n                  </ul>\r\n                  For further assistance, contact your administrator. \r\n    ",
      strMonthArray: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
      Total: "Total",
      strHiddenAnswerPipingText: "Answer(s) to this question are hidden due to answer piping settings.</ br > Please satisfy the conditions on previous pages to view the complete question.",
      AcceptCookieHeader: "Cookie Confirmation",
      AcceptCookieMessage: "Cookies are a required tool to ensure smooth participation. To review cookie permissions, see <MP>",
      AcceptCookieButton: "Accept All Cookies",
      strManagePreference: "Manage Preference.",
      strChooseCookies: "Please choose which cookies to allow.",
      strFuctionalCookies: "Functional Cookies",
      strAlwaysActive: "Always Active",
      strConsentSmoothParticipation: "These cookies are critical to ensure smooth participation.",
      strConsentChooseLanguage: "Choose the appropriate participation language",
      strConsentBrowserSupport: "Checks if your browser supports cookies",
      strConsentMaintainSession: "Maintains participation session",
      strConsentTracksStatus: "Tracks your participation status",
      strConsentRestrictedParticipation: "Restricts repeated participation",
      strConsentDownloadCookie: "Shows that the survey download has started",
      strConsentCookie: "Remembers that you consented to cookies",
      strAnalyticsCookies: "Analytics Cookies",
      strAnalyticsCookiesGoogle: " We use these cookies to track participation in Google Analytics.",
      strConsentGoogleAnalytics: "Tracks your participation in Google Analytics",
      strAcceptCookieChoices: "I accept these choices",
      strCookieCancle: "Cancel",
      CurrencyNegativeValueMessage: "Currency cannot be negative.",
      ReenterTextMulti: "Re-enter email address",
      Signature_SaveConfirm: "Save to Confirm",
      Signature_Clear: "Clear",
      Signature_ClickSave: "Click Save to Confirm",
      Signature_FileSizeExeedMessage: "The file to upload cannot exceed 5 MB.",
      Signature_FileTypeMessage: "Please upload a .jpg,.jpeg or .png file.",
      Signature_TapSignHere: "Tap to sign here.",
      Signature_ClickSignHere: "Click to sign here.",
      Signature_UploadSignature: "Upload Signature",
      Signature_TypeHere: "Type Here",
      Participation_Nomatchfound: "No match found.",
      Participation_ChooseLanguage: "Choose Language",
      RS_TypeValue: "Enter rating​",
      Title: null
    };

    // Update the survey messages
    this.updateSurveyMessages(defaultMessages);
    

  }





  /**
   * Get matrix data for a group (needed by the matrix pipe)
   */
  getMatrixData(group: Group): Matrix {
    if (group && group.Matrix_id) {
      // Check if we have Matrix data in the survey
      const matrixData = this.surveyData?.Matrix || (this.surveyData as any)?.SurveyData?.Matrix;
      if (matrixData) {
        // Find the actual matrix data
        const actualMatrix = matrixData.find(m => m.matrix_id === group.Matrix_id);
        if (actualMatrix) {
          // Convert SurveyMatrix to Matrix
          return {
            matrix_id: actualMatrix.matrix_id,
            Mtitle: actualMatrix.Mtitle || 'Matrix',
            PipeMTitle: actualMatrix.PipeMTitle || '',
            questiontip: actualMatrix.questiontip || '',
            QPos: actualMatrix.QPos || 0,
            isMulti: actualMatrix.isMulti || false,
            MultiColumnCount: actualMatrix.MultiColumnCount || 0,
            subq_col_width: actualMatrix.subq_col_width || 0,
            totalwidth: actualMatrix.totalwidth || 0,
            VerticalSpaceCount: actualMatrix.VerticalSpaceCount || 0,
            IsSingleGrid: actualMatrix.IsSingleGrid || false,
            isBranchEnabled: actualMatrix.isBranchEnabled || false,
            SourceGrid: actualMatrix.SourceGrid || 0,
            SourcePosition: actualMatrix.SourcePosition || 0,
            SourceNumber: actualMatrix.SourceNumber || 0,
            TargetGrid: typeof actualMatrix.TargetGrid === 'number' ? actualMatrix.TargetGrid : 0,
            isDisplayManage: actualMatrix.isDisplayManage || false,
            isOverAllStyleApplied: actualMatrix.isOverAllStyleApplied || false,
            isHiddenQ: actualMatrix.isHiddenQ || false
          };
        }
      }
    }
    
    // Return default matrix structure if no actual data found
    return {
      matrix_id: 1,
      Mtitle: 'Default Matrix',
      PipeMTitle: '',
      questiontip: '',
      QPos: 0,
      isMulti: false,
      MultiColumnCount: 0,
      subq_col_width: 0,
      totalwidth: 0,
      VerticalSpaceCount: 0,
      IsSingleGrid: false,
      isBranchEnabled: false,
      SourceGrid: 0,
      SourcePosition: 0,
      SourceNumber: 0,
      TargetGrid: 0,
      isDisplayManage: false,
      isOverAllStyleApplied: false,
      isHiddenQ: false
    };
  }

  /**
   * Get group data for a question
   */
  private getGroupDataByNumber(groupNo: number): SurveyGroup | null {
    const groupsData = this.surveyData?.Groups || (this.surveyData as any)?.SurveyData?.Groups;
    if (groupsData) {
      return groupsData.find((e) => e.group_no === groupNo) || null;
    }
    return null;
  }

  /**
   * Get matrix data for a group
   */
  private getMatrixDataForGroup(group: SurveyGroup): SurveyMatrix | null {
    const matrixData = this.surveyData?.Matrix || (this.surveyData as any)?.SurveyData?.Matrix;
    if (matrixData && group) {
      return matrixData.find((e) => e.matrix_id === group.Matrix_id) || null;
    }
    return null;
  }

  /**
   * Get all groups for a matrix
   */
  private getGroupDatas(matrix: SurveyMatrix): SurveyGroup[] {
    const groupsData = this.surveyData?.Groups || (this.surveyData as any)?.SurveyData?.Groups;
    if (groupsData && matrix) {
      return groupsData.filter((e) => e.Matrix_id === matrix.matrix_id);
    }
    return [];
  }

     /**
    * Get questions for a specific group
    */
   private getQuestionsForGroup(groupNo: number): Questions[] {
     if (this.surveyData?.Pages && this.currentPageIndex >= 0) {
       const currentPage = this.surveyData.Pages[this.currentPageIndex];
       if (currentPage?.Questions) {
         // Filter questions by group_no and convert to Questions type
         const filteredQuestions = currentPage.Questions.filter((e) => e.group_no === groupNo);
         
         // Convert SurveyQuestion to Questions type
         return filteredQuestions.map(sq => {
           // Create a basic Questions object with required properties
           const question = new Questions();
           question.qno = parseInt(sq.QNo);
           question.qtitle = sq.Title;
           question.qtype = sq.QuestionType;
           question.qsubtype = parseInt(sq.SubQuestionType || '0');
           question.group_no = sq.group_no || 0;
           
           // Convert answers if they exist
           if (sq.Answers) {
             question.Answers = sq.Answers.map(ans => {
               const answer = new Answers();
               answer.value_id = parseInt(ans.ValueID);
               answer.value_text = ans.AnswerText;
               answer.weight = parseFloat(ans.Weight || '0');
               return answer;
             });
           } else {
             question.Answers = [];
           }
           
           // Set default validation state
           question.isValid = false;
           
           return question;
         });
       }
     }
     return [];
   }

   /**
    * Get original question data from survey data by question number
    * This is needed to access the original Rows data for Multiple Text Box questions
    */
   private getOriginalQuestionData(qno: number): any {
     if (this.surveyData?.Pages) {
       for (const page of this.surveyData.Pages) {
         if (page.Questions) {
           const question = page.Questions.find(q => parseInt(q.QNo) === qno);
           if (question) {
             return question;
           }
         }
       }
     }
     return null;
   }

  /**
   * Prevent default event behavior (used in form)
   */
  preventEvent(event: any): void {
    event.preventDefault();
  }

  /**
   * Get button alignment width for navigation footer
   * Matches the method from participation.component.ts
   */
  defaultBtnAlignWidth(): any {
    return {
      'text-align': 'center',
      'width': '100%'
    };
  }

  /**
   * Submit survey - show thank you page
   */
  submitSurvey(): void {
    this.showThankYou = true;
    this.showQuestions = false;
    this.showWelcome = false;
  }

  /**
   * Create a Questions object for the welcome message
   * This is used by the DescriptiveTextComponent
   */
  getWelcomeQuestionJson(): string {
    const welcomeQuestion = new Questions();
    welcomeQuestion.qno = 0; // Welcome message has no question number
    welcomeQuestion.qtitle = this.surveyData?.WelcomeText || 'Welcome to the survey!';
    welcomeQuestion.qtype = 'CM'; // Comment/Descriptive text type
    welcomeQuestion.qsubtype = 0; // Regular descriptive text
    welcomeQuestion.UniqueNumber = 1001; // Unique number for welcome message
    welcomeQuestion.isOverAllStyleApplied = true;
    welcomeQuestion.mandatory = false;
    welcomeQuestion.Answers = [];
    
    // Set default validation state
    welcomeQuestion.isValid = false;
    
    return JSON.stringify(welcomeQuestion);
  }

  /**
   * Restart the survey from the beginning
   */
  restartSurvey(): void {
    this.showThankYou = false;
    this.showQuestions = false;
    this.showWelcome = true;
    this.currentPageIndex = 0;
    if (this.surveyData?.Pages && this.surveyData.Pages.length > 0) {
      this.currentPage = this.surveyData.Pages[0];
    }
  }

}
